import * as React from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  getDay,
  setMonth,
  setYear,
} from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'

interface CalendarProps {
  selected?: Date
  onSelect?: (_date: Date | undefined) => void
  disabled?: (_date: Date) => boolean
  locale?: Locale
  className?: string
  defaultMonth?: Date
}

type ViewMode = 'days' | 'months' | 'years'

// Months for the grid (lowercase with period)
const MONTHS_GRID = [
  'ene.',
  'feb.',
  'mar.',
  'abr.',
  'may.',
  'jun.',
  'jul.',
  'ago.',
  'sep.',
  'oct.',
  'nov.',
  'dic.',
]

// Months for the header (capitalized, no period)
const MONTHS_HEADER = [
  'Ene',
  'Feb',
  'Mar',
  'Abr',
  'May',
  'Jun',
  'Jul',
  'Ago',
  'Sep',
  'Oct',
  'Nov',
  'Dic',
]

const WEEKDAY_NAMES = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do']

export function Calendar({
  selected,
  onSelect,
  disabled,
  locale = es,
  className,
  defaultMonth,
}: CalendarProps) {
  const [currentDate, setCurrentDate] = React.useState<Date>(
    selected || defaultMonth || new Date()
  )
  const [viewMode, setViewMode] = React.useState<ViewMode>('days')

  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  // Generate days for current month - always return 42 days (6 weeks)
  // Start week on Monday (1) instead of Sunday (0)
  const generateDays = () => {
    const start = startOfMonth(currentDate)
    const end = endOfMonth(currentDate)
    const days = eachDayOfInterval({ start, end })

    // getDay returns 0 (Sunday) to 6 (Saturday)
    // We need 0 (Monday) to 6 (Sunday), so adjust
    const dayOfWeek = getDay(start)
    const firstDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1

    // Add days from previous month
    const prevMonthDays: Date[] = []
    for (let i = firstDayOfWeek - 1; i >= 0; i--) {
      const prevDay = new Date(start)
      prevDay.setDate(prevDay.getDate() - (i + 1))
      prevMonthDays.push(prevDay)
    }

    // Add days from next month to complete 42 days (6 rows × 7 days)
    const totalDays = prevMonthDays.length + days.length
    const nextMonthDays: Date[] = []
    for (let i = 1; i <= 42 - totalDays; i++) {
      const nextDay = new Date(end)
      nextDay.setDate(nextDay.getDate() + i)
      nextMonthDays.push(nextDay)
    }

    return [...prevMonthDays, ...days, ...nextMonthDays]
  }

  const days = viewMode === 'days' ? generateDays() : []

  // Pagination state for years only
  const [yearPage, setYearPage] = React.useState(0)

  // Get all 12 months + 4 more for the 4th row (16 total)
  const getCurrentMonths = () => {
    const months: { name: string; index: number; isNextYear: boolean }[] = []

    // First 12 months of current year
    for (let i = 0; i < 12; i++) {
      months.push({
        name: MONTHS_GRID[i],
        index: i,
        isNextYear: false,
      })
    }

    // Next 4 months from following year
    for (let i = 0; i < 4; i++) {
      months.push({
        name: MONTHS_GRID[i],
        index: i,
        isNextYear: true,
      })
    }

    return months
  }

  // Get 16 years for current page (4 rows × 4 years)
  const getCurrentYears = () => {
    const baseYear = currentYear + yearPage * 16
    const years: number[] = []
    for (let i = 0; i < 16; i++) {
      years.push(baseYear + i)
    }
    return years
  }

  const handlePrevious = () => {
    if (viewMode === 'days') {
      setCurrentDate(subMonths(currentDate, 1))
    } else if (viewMode === 'months') {
      // Move back 1 year
      const newDate = setYear(currentDate, currentYear - 1)
      setCurrentDate(newDate)
    } else if (viewMode === 'years') {
      setYearPage(yearPage - 1)
    }
  }

  const handleNext = () => {
    if (viewMode === 'days') {
      setCurrentDate(addMonths(currentDate, 1))
    } else if (viewMode === 'months') {
      // Move forward 1 year
      const newDate = setYear(currentDate, currentYear + 1)
      setCurrentDate(newDate)
    } else if (viewMode === 'years') {
      setYearPage(yearPage + 1)
    }
  }

  const handleMonthSelect = (monthIndex: number, isNextYear: boolean) => {
    let newDate = setMonth(currentDate, monthIndex)
    // If selecting from next year's months, advance the year
    if (isNextYear) {
      newDate = setYear(newDate, currentYear + 1)
    }
    setCurrentDate(newDate)
    setViewMode('days')
  }

  const handleYearSelect = (year: number) => {
    const newDate = setYear(currentDate, year)
    setCurrentDate(newDate)
    setViewMode('days')
    setYearPage(0)
  }

  const handleDaySelect = (day: Date) => {
    if (disabled && disabled(day)) return
    onSelect?.(day)
  }

  const isDateDisabled = (day: Date) => {
    return disabled ? disabled(day) : false
  }

  return (
    <div className={cn('flex w-[224px] flex-col gap-4 p-0', className)}>
      {/* Header */}
      <div className="flex h-8 items-center gap-2">
        {/* Previous Button */}
        <button
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md bg-[#FAFAFA]',
            'transition-colors hover:bg-neutral-100'
          )}
          onClick={handlePrevious}
          type="button"
        >
          <ChevronLeft className="h-4 w-4 text-[#262626] [stroke-width:1.33px]" />
        </button>

        {/* Center content */}
        <div className="flex flex-1 items-center justify-center gap-2">
          {/* Month Button/Text */}
          {viewMode === 'months' ? (
            <span
              className={cn(
                'flex h-8 min-w-8 flex-1 items-center justify-center px-3',
                'text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]'
              )}
            >
              {MONTHS_HEADER[currentMonth]}
            </span>
          ) : (
            <button
              className={cn(
                'flex h-8 min-w-8 flex-1 items-center justify-center gap-2 rounded-md',
                'border border-[#D4D4D4] bg-[#FAFAFA] px-3',
                'text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]',
                'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]',
                'transition-colors hover:bg-neutral-50'
              )}
              onClick={() => setViewMode('months')}
              type="button"
            >
              {MONTHS_HEADER[currentMonth]}
            </button>
          )}

          {/* Year Button/Text */}
          {viewMode === 'years' ? (
            <span
              className={cn(
                'flex h-8 min-w-8 flex-1 items-center justify-center px-3',
                'text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]'
              )}
            >
              {currentYear}
            </span>
          ) : (
            <button
              className={cn(
                'flex h-8 min-w-8 flex-1 items-center justify-center gap-2 rounded-md',
                'border border-[#D4D4D4] bg-[#FAFAFA] px-3',
                'text-sm font-medium leading-5 tracking-[0.005em] text-[#262626]',
                'shadow-[0px_1px_2px_rgba(0,0,0,0.05)]',
                'transition-colors hover:bg-neutral-50'
              )}
              onClick={() => {
                setYearPage(0)
                setViewMode('years')
              }}
              type="button"
            >
              {currentYear}
            </button>
          )}
        </div>

        {/* Next Button */}
        <button
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md bg-[#FAFAFA]',
            'transition-colors hover:bg-neutral-100'
          )}
          onClick={handleNext}
          type="button"
        >
          <ChevronRight className="h-4 w-4 text-[#262626] [stroke-width:1.33px]" />
        </button>
      </div>

      {/* Content Grid - Fixed height for all views */}
      <div className="h-[224px] w-[224px]">
        {viewMode === 'days' && (
          <div className="flex flex-col">
            {/* Weekday Names */}
            <div className="flex h-8">
              {WEEKDAY_NAMES.map((day, index) => (
                <div
                  className="flex h-8 w-8 items-center justify-center rounded"
                  key={index}
                >
                  <span className="text-xs font-normal leading-[150%] tracking-[0.015em] text-[#737373]">
                    {day}
                  </span>
                </div>
              ))}
            </div>

            {/* Days Grid - Always 6 rows */}
            <div className="flex flex-col">
              {[0, 1, 2, 3, 4, 5].map((weekIndex) => (
                <div className="flex h-8" key={weekIndex}>
                  {days.slice(weekIndex * 7, weekIndex * 7 + 7).map((day) => {
                    const isSelected = selected && isSameDay(day, selected)
                    const isCurrentMonth = isSameMonth(day, currentDate)
                    const isTodayDate = isToday(day)
                    const isDayDisabled = isDateDisabled(day)

                    return (
                      <button
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded',
                          'text-sm font-normal leading-[150%] tracking-[0.005em]',
                          'transition-colors',
                          isSelected &&
                            'rounded-[5px] bg-[#1E293B] text-[#FAFAFA]',
                          !isSelected &&
                            isCurrentMonth &&
                            'text-[#262626] hover:bg-neutral-100',
                          !isSelected &&
                            !isCurrentMonth &&
                            'text-[#262626] opacity-50',
                          isTodayDate && !isSelected && 'font-medium',
                          isDayDisabled && 'cursor-not-allowed opacity-50'
                        )}
                        disabled={isDayDisabled}
                        key={day.toISOString()}
                        onClick={() => handleDaySelect(day)}
                        type="button"
                      >
                        {format(day, 'd', { locale })}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        {viewMode === 'months' && (
          <div className="flex h-[224px] flex-col justify-between gap-4">
            {/* 4 rows of 4 months each (12 current year + 4 next year) */}
            {[0, 1, 2, 3].map((rowIndex) => (
              <div
                className="mx-auto flex h-8 w-[224px] items-center gap-4"
                key={rowIndex}
              >
                {getCurrentMonths()
                  .slice(rowIndex * 4, rowIndex * 4 + 4)
                  .map(({ name, index, isNextYear }) => {
                    const isSelected = index === currentMonth && !isNextYear

                    return (
                      <button
                        className={cn(
                          'flex h-8 w-11 flex-1 items-center justify-center',
                          'text-sm font-normal leading-[150%] tracking-[0.005em]',
                          'transition-colors',
                          isSelected &&
                            'rounded-[5px] bg-[#1E293B] text-[#FAFAFA]',
                          !isSelected &&
                            !isNextYear &&
                            'text-[#262626] hover:bg-neutral-100',
                          !isSelected &&
                            isNextYear &&
                            'text-[#737373] hover:bg-neutral-100'
                        )}
                        key={`${isNextYear ? 'next' : 'current'}-${index}`}
                        onClick={() => handleMonthSelect(index, isNextYear)}
                        type="button"
                      >
                        {name}
                      </button>
                    )
                  })}
              </div>
            ))}
          </div>
        )}

        {viewMode === 'years' && (
          <div className="flex h-[224px] flex-col justify-between gap-4">
            {/* 4 rows of 4 years each */}
            {[0, 1, 2, 3].map((rowIndex) => (
              <div
                className="mx-auto flex h-8 w-[224px] items-center gap-4"
                key={rowIndex}
              >
                {getCurrentYears()
                  .slice(rowIndex * 4, rowIndex * 4 + 4)
                  .map((year) => (
                    <button
                      className={cn(
                        'flex h-8 w-11 flex-1 items-center justify-center',
                        'text-sm font-normal leading-[150%] tracking-[0.005em]',
                        'transition-colors',
                        year === currentYear &&
                          'rounded-[5px] bg-[#1E293B] text-[#FAFAFA]',
                        year !== currentYear &&
                          'text-[#262626] hover:bg-neutral-100'
                      )}
                      key={year}
                      onClick={() => handleYearSelect(year)}
                      type="button"
                    >
                      {year}
                    </button>
                  ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
